import { format, isBefore } from "date-fns";
import React from "react";

import {
  Anchor,
  Container,
  Dialog,
  Hoverable,
  Popover,
  Row,
  Spacing,
  Text,
  useToast,
  stylex,
  cssVariables,
} from "../../components";
import { Pressable } from "../../components/pressable";
import { useToggle } from "../../hooks/use_toggle";
import {
  useCancelBooking,
  useGetPartnerOrder,
  useGetSpace,
  useInvalidateSpaceCache,
} from "../../data/api";
import { Booking, BookingType, OrderStatus } from "../../data";
import { parseTime } from "../../lib/datetime";
import { OrderDetails } from "../../core/order_details";
import { formatCurrency } from "../../lib/currency/currency";
import { getSystemLocale } from "../../lib/locale/locale";
import { addMinutes, formatTime } from "../../offering/time_utils";
import { Button } from "react-bootstrap";

interface BookingPreviewProps {
  booking: Booking;
  onHoverIn?: () => void;
  onHoverOut?: () => void;
  includeSpaceName: boolean;
}

export function BookingPreview(props: BookingPreviewProps) {
  const {
    booking,
    onHoverIn = () => {},
    onHoverOut = () => {},
    includeSpaceName,
  } = props;
  const order = useGetPartnerOrder(booking.orderID);
  const [isPopoverOpen, popover] = useToggle();
  const space = useGetSpace(booking.spaceID);
  const cancelBooking = useCancelBooking();
  const invalidateSpaceCache = useInvalidateSpaceCache();
  const [cancel, setCancel] = React.useState({
    loading: false,
    canceled: false,
  });

  const [viewBookingIsOpen, viewBookingDialog] = useToggle();
  const [cancelIsOpen, cancelDialog] = useToggle();
  const toast = useToast();

  const handleHoverIn = React.useCallback(async () => {
    onHoverIn();
    popover.setTrue();
  }, [onHoverIn, popover]);

  const handleHoverOut = React.useCallback(async () => {
    onHoverOut();
    popover.setFalse();
  }, [onHoverOut, popover]);

  const handleCancelConfirmationClose = React.useCallback(async () => {
    if (cancel.canceled) {
      await invalidateSpaceCache(space.id);
    }

    cancelDialog.setFalse();
  }, [invalidateSpaceCache, cancel, cancelDialog, space.id]);

  const handleCancelConfirm = React.useCallback(async () => {
    setCancel({ loading: true, canceled: false });

    try {
      await cancelBooking(booking.id);
      setCancel({ loading: false, canceled: true });
    } catch (error: any) {
      toast.error({
        message: error.message || "Something went wrong",
      });
      setCancel({ loading: false, canceled: false });
    }
  }, [booking.id, cancelBooking, toast]);

  let start = "";
  let end = "";

  if (booking.bookingType === BookingType.Daily && booking.time) {
    start = format(parseTime(booking.time), "ha");
  } else if (booking.bookingType === BookingType.Hourly) {
    const startTime = booking.time;
    const endTime = addMinutes(startTime, Number(booking.duration) * 60);

    start = formatTime(startTime);
    end = formatTime(endTime);
  }

  const isCancellable =
    booking.cancellationAllowed &&
    booking.cancellationAllowedBefore &&
    isBefore(new Date(), booking.cancellationAllowedBefore);

  return (
    <>
      <Hoverable
        useWrapper
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}
        style={{ height: "100%" }}
      >
        <Popover
          isOpen={isPopoverOpen}
          onRequestClose={handleHoverOut}
          content={
            <Container
              backgroundColor="white"
              minWidth={264}
              padding={16}
              boxShadow={cssVariables.shadow}
              borderWidth={1}
              borderColor={cssVariables.borderColorDefault}
              borderRadius={cssVariables.radius}
            >
              {order.guestName && (
                <Text isBold fontSize="sm">
                  {order.guestName}
                </Text>
              )}
              {order.guestCompanyName && (
                <Text fontSize="sm">{order.guestCompanyName}</Text>
              )}
              {order.guestEmail && (
                <Anchor href={`mailto:${order.guestEmail}`}>
                  <Text color="link" fontSize="sm">
                    {order.guestEmail}
                  </Text>
                </Anchor>
              )}
              {order.guestPhoneNumber && (
                <Text fontSize="sm">{order.guestPhoneNumber}</Text>
              )}
              <Spacing height={16} />
              {includeSpaceName && <Text fontSize="sm">{space.name}</Text>}
              {order.status === OrderStatus.Confirmed && (
                <Text fontSize="sm">Confirmed without payment</Text>
              )}
              <Text fontSize="sm" color="muted">
                Total{" "}
                <Text fontSize="sm" isBold>
                  {formatCurrency(
                    order.totalPrice,
                    order.currencyCode,
                    getSystemLocale()
                  )}
                </Text>
              </Text>
              <Spacing height={24} />
              {!isCancellable && booking.cancellationAllowedBefore && (
                <Text fontSize="xs">
                  This booking cannot be cancelled after{" "}
                  {booking.cancellationAllowedBefore.toLocaleString("en-US", {
                    timeZone: space.locationTimeZone,
                  })}
                  .
                </Text>
              )}
            </Container>
          }
        >
          {({ ref }) => (
            <div style={styles("booking")} ref={ref as any}>
              <Pressable
                testID="booking-preview"
                onPress={viewBookingDialog.setTrue}
                style={{ height: "100%", width: "100%", overflow: "hidden" }}
              >
                <Container expanded padding={4}>
                  <Text fontSize="sm">
                    {start} {end ? `- ${end}` : ""}
                  </Text>
                  <Text isBold fontSize="sm">
                    {order.guestName}
                  </Text>
                  {includeSpaceName && <Text fontSize="sm">{space.name}</Text>}
                </Container>
              </Pressable>
            </div>
          )}
        </Popover>
      </Hoverable>
      <Dialog
        isOpen={viewBookingIsOpen}
        onRequestClose={viewBookingDialog.setFalse}
      >
        <Container minWidth={600} padding={40}>
          <Text fontSize="xl" isBold>
            Booking details
          </Text>
          <Spacing height={16} />
          <OrderDetails view="admin" order={order} />
          <Spacing height={40} />
          {!isCancellable && booking.cancellationAllowedBefore && (
            <Text fontSize="xs">
              This booking cannot be cancelled after{" "}
              {booking.cancellationAllowedBefore.toLocaleString("en-US", {
                timeZone: space.locationTimeZone,
              })}
              .
            </Text>
          )}
        </Container>
      </Dialog>
      <Dialog
        isOpen={cancelIsOpen}
        onRequestClose={handleCancelConfirmationClose}
      >
        <Container width={666} padding={40}>
          {cancel.canceled ? (
            <>
              <Text isBold fontSize="xl">
                Booking cancelled!
              </Text>
              <Spacing height={16} />
              <Text>
                A cancellation email has been sent to {order.guestEmail}
              </Text>
              <Spacing height={24} />
              <Row justifyContent="flex-end">
                <Button
                  data-testid="got-it"
                  onClick={handleCancelConfirmationClose}
                >
                  Got it
                </Button>
              </Row>
            </>
          ) : (
            <>
              <Text isBold fontSize="xl">
                Cancel booking
              </Text>
              <Spacing height={16} />
              <Text>
                Are you sure you want to cancel {order.guestName}’s booking?
              </Text>
              <Text>
                Once cancelled they will receive a cancellation email and their
                booking will no longer exist
              </Text>
              <Spacing height={24} />
              <Row justifyContent="flex-end">
                <Button
                  onClick={cancelDialog.setFalse}
                  variant="outline-primary"
                  disabled={cancel.loading}
                >
                  No
                </Button>
                <Spacing width={16} />
                <Button
                  data-testid="cancel-yes"
                  onClick={handleCancelConfirm}
                  disabled={cancel.loading}
                >
                  {cancel.loading ? "Cancelling..." : "Yes"}
                </Button>
              </Row>
            </>
          )}
        </Container>
      </Dialog>
    </>
  );
}

const styles = stylex.create({
  base: {},
  booking: {
    height: "calc(100% - 1px)",
    backgroundColor: "#ffe499",
    borderRadius: cssVariables.radius,
    borderWidth: 1,
    borderColor: cssVariables.borderColorDark,
    borderStyle: "solid",
  },
});
